import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SequenceLabelModel } from '../../../_models/ImagingProject/sequence-label-model';

export interface NavigatorSeriesModel {
  id: number;
  visitId: number;
  visitConfigId: number;
  label: string;
  timepoint?: string;
  color?: string;
}

@Component({
  selector: 'app-inner-series-navigator',
  templateUrl: './inner-series-navigator.component.html',
  styleUrls: ['./inner-series-navigator.component.css']
})
export class InnerSeriesNavigatorComponent implements OnInit, OnChanges {

  @Input() series: NavigatorSeriesModel[];
  @Input() visitConfig: number;
  @Input() openedViewers: number;
  @Input() availableSequenceLabels: SequenceLabelModel[];
  @Input() defaultSequenceLabelIds: number[];
  @Input() vertical: boolean = false;
  
  @Output() public openViewer: EventEmitter<any> = new EventEmitter<any>();
  @Output() public closeAllViewers: EventEmitter<any> = new EventEmitter<any>();
  @Output() public isOpenImages: EventEmitter<any> = new EventEmitter<any>();
  @Output() public visitedSeriesCounter: EventEmitter<any> = new EventEmitter<any>();

  selectedSequenceLabels: SequenceLabelModel[] = [];

  filteredSeries: any[] = [];

  blockIndex = 0;
  blockSize = 1;

  visitedSeriesCount: number;

  constructor() {
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!this.availableSequenceLabels && !!this.defaultSequenceLabelIds && !!this.series) {
      this.applyDefaultSequenceLabels();
    }
  }

  applyDefaultSequenceLabels(): void {
    this.selectedSequenceLabels = this.availableSequenceLabels
      .filter(studyLabel => !!this.defaultSequenceLabelIds.find(id => id === studyLabel.id));
    this.filterSeries();
    this.setSeriesColors();
  }

  filterSeries(): void {
    this.filteredSeries = this.series
      .filter(s => this.selectedSequenceLabels.find(l => l.name.toLowerCase() === s.label.toLowerCase()));
  }

  getSeriesSelectionColor(series): string {
    if (series.openInViewerNow) {
      return '#f9bf91';
    } else if (series.openedInViewer) {
      return '#c1c1c1';
    }
    // default color
    return '#efefef';
  }

  getSeriesColor(series): string {
    try {
      return series.color ? series.color : '#000000';
    } catch (error) {
      console.log("==> error... ", error);
    }
  }

  setSeriesColors() {
    if (this.filteredSeries.length > 0 && !this.filteredSeries[0].color) {
      let colors = ['MediumVioletRed', 'OrangeRed', 'ForestGreen',
        'DarkTurquoise', 'Brown', 'DodgerBlue','OliveDrab', 'Navy', 'LimeGreen', 'SlateBlue'];
      const sort = Math.random() - 0.5;
      colors = colors.sort(() => sort);
      const visits = [];
      let visitColor = {};
      let counter = 0;
      for (let i = 0; i < this.filteredSeries.length; i++) {
        let t = this.filteredSeries[i].timepoint;
        if(t == undefined){
          t = this.filteredSeries[i].seriesVisitName;
        }
        if(t != undefined){
          if (!visits.includes(t)) {
            visits.push(t);
            visitColor[t] = colors[counter];
            this.filteredSeries[i].color = visitColor[t]
            counter++;
            if (counter > colors.length) {
              counter = 0;
            }
          } else {
            this.filteredSeries[i].color = visitColor[t];
          }
        }
      } 
    }
  }

  isSeriesFirst(seriesIndex) {
    return seriesIndex === this.blockIndex;
  }

  isSeriesMiddle(seriesIndex) {
    return ((seriesIndex > this.blockIndex) && (seriesIndex < this.blockIndex + this.blockSize - 1));
  }

  isSeriesLast(seriesIndex) {
    return (seriesIndex === this.blockIndex + this.blockSize - 1) ||
      (this.blockIndex + this.blockSize - 1 >= this.series.length && seriesIndex === this.series.length - 1);
  }

  isSeriesFirstAndLast(seriesIndex) {
    return (seriesIndex === this.series.length - 1 && seriesIndex === this.blockIndex);
  }

  onOpenViewerClick(series) {
    this.doOpenViewer([series.id]);
    this.markSeriesAsOpened(series);
  }

  dragstart(ev, seriesId){
    // console.log("dragstart...");
    // console.log("seriesId:", seriesId);
    // console.log("seriesInstanceUID:", seriesInstanceUID);
    ev.dataTransfer.setData("seriesId", seriesId);
    // ev.dataTransfer.setData("seriesInstanceUID", seriesInstanceUID);
  }

  onPreviousFrame() {
    this.blockIndex = this.blockIndex - this.blockSize;
    if (this.blockIndex < 0) {
      this.blockIndex = 0;
    }
    this.closeAllViewers.emit();
    this.openBlockSeries();
  }

  onNextFrame() {
    this.blockIndex = this.blockIndex + this.blockSize;
    if (this.blockIndex >= this.series.length) {
      this.blockIndex = this.series.length - this.blockSize;
    }
    this.openBlockSeries();
  }

  onApplyFrame() {
    this.resetIndex();
    this.closeAllViwers();
    this.openBlockSeries();
  }

  onFrameLengthChange(): void {
    if (this.blockSize > 9 || this.blockSize > this.series.length) {
      if (this.series.length >= 9) {
        this.blockSize = 9;
      } else {
        this.blockSize = this.series.length;
      }
    }
    if (this.blockSize < 1) {
      this.blockSize = 1;
    }
  }

  closeAllViwers() {
    if (this.closeAllViewers != null) {
      this.closeAllViewers.emit();
    }
  }

  doOpenViewer(seriesIds) {
    if (this.openViewer != null) {
      this.openViewer.emit(seriesIds);
    }
  }

  openBlockSeries() {
    const seriesIds = [];
    if (this.filteredSeries != null) {
      this.closeAllViewers.emit();
      this.filteredSeries.forEach((element, index) => {
        this.unmarkSeriesAsOpened(element);

        if ((index >= this.blockIndex) && (index < (this.blockIndex + this.blockSize))) {
          this.markSeriesAsOpened(element);
          seriesIds.push(element.id);
        }
      });
    }
    if (seriesIds.length > 0) {
      this.closeAllViwers();
      this.doOpenViewer(seriesIds);
    }
  }

  markSeriesAsOpened(series) {
    if (this.openedViewers < 9) {
      series['openInViewerNow'] = true;
      series['openedInViewer'] = true;
    }
    this.validateIsOpenImages();
  }

  unmarkSeriesAsOpened(series) {
    if (series != null) {
      series['openInViewerNow'] = false;
    }
  }

  unmarkSeriesAsOpenedBySeriesId(seriesId) {
    const selectedSeries = this.series.find(s => s.id === seriesId);
    if (selectedSeries != null) {
      selectedSeries['openInViewerNow'] = false;
    }
    this.validateIsOpenImages();
  }

  validateIsOpenImages() {
    let ans = false;
    this.visitedSeriesCount = 0;
    if (this.series != null) {
      this.series.forEach((element, index) => {
        if (element['openInViewerNow'] === true || element['openedInViewer'] === true) {
          ans = true;
          this.visitedSeriesCount = this.visitedSeriesCount + 1;
        }

      });
    }
    this.visitedSeriesCounter.emit(this.visitedSeriesCount);
    this.isOpenImages.emit(ans);
  }

  resetIndex() {
    this.blockIndex = 0;
  }

}
